import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Button, DialogContent, Divider, LinearProgress, Stack, Typography, } from "@mui/material";
import { AlertsBarContainer, AlertsContext, DistaDialog, DistaDialogHeader, } from "@sista/dista-ui";
import { SmartButton, useSafeSistaAsync, } from "@sista/library-stasi";
import { RenderTagsWithMenu } from "@sista/service-tagger-ui";
import { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSpravaTextuClient } from "../../util/useSpravaTextuClient";
import { applyVariantUrns, SelectVariantUrns, } from "../parts/SelectVariantUrns";
const emptyUrnsData = {
    providerUrns: [],
    programUrns: [],
    cfpUrns: [],
};
const taggerNamespace = "spratex";
export const MassEditationDialog = ({ texts, tree, onClose, onDone, }) => {
    const { t } = useTranslation("sprava-textu");
    const { showSuccess, showInfo } = useContext(AlertsContext);
    const [selectedTags, setSelectedTags] = useState([]);
    const [showProgressBar, setShowProgressBar] = useState(false);
    const [progressValue, setProgressValue] = useState(0);
    const [urnsData, setUrnsData] = useState(emptyUrnsData);
    const onDoneHandler = () => {
        if (onDone) {
            onDone();
        }
        onClose();
    };
    const spratex = useSpravaTextuClient();
    const [addTagsCall, addTagsState] = useSafeSistaAsync(spratex.addTagg);
    const [removeTagsCall, removeTagsState] = useSafeSistaAsync(spratex.removeTag);
    const [needReviewStatusCall, needReviewStatusState] = useSafeSistaAsync(spratex.setNeedReview);
    const [updateTextCall, updateTextState] = useSafeSistaAsync(spratex.updateText);
    const addTagsHandler = useCallback(async (texts, tagIds) => {
        if (tagIds.length === 0) {
            showInfo(t("sprava-textu.massEdit.noTagsSelected", "Žádné štítky nebyly vybrány"));
            return;
        }
        setShowProgressBar(true);
        setProgressValue(0);
        for (let i = 0; i < texts.length; i++) {
            const textNamespace = texts[i].namespace;
            const textKey = texts[i].key;
            const data = Object.assign(tagIds, {
                namespace: textNamespace,
                key: textKey,
            });
            await addTagsCall(data);
            setProgressValue(((i + 1) / texts.length) * 100);
        }
        setShowProgressBar(false);
        showSuccess(t("sprava-textu.massEdit.addTagsSuccess", "Štítky úspěšně přidány"));
        setProgressValue(0);
    }, [addTagsCall, showSuccess, t, showInfo]);
    const removeTagsHandler = useCallback(async (texts, tagIds) => {
        if (tagIds.length === 0) {
            showInfo(t("sprava-textu.massEdit.noTagsSelected", "Žádné štítky nebyly vybrány"));
            return;
        }
        setShowProgressBar(true);
        setProgressValue(0);
        for (let i = 0; i < texts.length; i++) {
            const textNamespace = texts[i].namespace;
            const textKey = texts[i].key;
            const data = Object.assign(tagIds, {
                namespace: textNamespace,
                key: textKey,
            });
            await removeTagsCall(data);
            setProgressValue(((i + 1) / texts.length) * 100);
        }
        setShowProgressBar(false);
        showSuccess(t("sprava-textu.massEdit.removeTagsSuccess", "Štítky úspěšně odebrány"));
        setProgressValue(0);
    }, [removeTagsCall, showSuccess, t, showInfo]);
    const addVariantHandler = useCallback(async (texts) => {
        if (urnsData.providerUrns.length === 0) {
            showInfo(t("sprava-textu.massEdit.noVariantSelected", "Nebyla vybrána žádná varianta"));
            return;
        }
        setShowProgressBar(true);
        setProgressValue(0);
        for (let i = 0; i < texts.length; i++) {
            const textNamespace = texts[i].namespace;
            const textKey = texts[i].key;
            const defaultTextVariant = texts[i].texts?.find((t) => t.providerUrn === null &&
                t.programUrn === null &&
                t.cfpUrn === null);
            const newVariant = applyVariantUrns(defaultTextVariant, urnsData);
            const allTextsData = [...texts[i].texts, newVariant];
            await updateTextCall({
                namespace: textNamespace,
                key: textKey,
                texts: allTextsData,
                taggerIds: texts[i].taggerIds,
            });
            setProgressValue(((i + 1) / texts.length) * 100);
        }
        setShowProgressBar(false);
        showSuccess(t("sprava-textu.massEdit.addVariantSuccess", "Varianty byly úspěšně přidány"));
        setProgressValue(0);
    }, [updateTextCall, urnsData, showSuccess, t, showInfo]);
    const needReviewStatusHandler = useCallback(async (texts) => {
        setShowProgressBar(true);
        setProgressValue(0);
        for (let i = 0; i < texts.length; i++) {
            const textNamespace = texts[i].namespace;
            const textKey = texts[i].key;
            await needReviewStatusCall({
                namespace: textNamespace,
                key: textKey,
            });
            setProgressValue(((i + 1) / texts.length) * 100);
        }
        setShowProgressBar(false);
        showSuccess(t("sprava-textu.massEdit.needReviewStatusSuccess", "Stav úspěšně změněn na 'Nutná revize'"));
        setProgressValue(0);
    }, [needReviewStatusCall, showSuccess, t]);
    return (_jsxs(DistaDialog, { open: true, onClose: onDoneHandler, children: [_jsx(DistaDialogHeader, { title: t("sprava-textu.massEdit.title", "Hromadná editace textů"), onClose: onDoneHandler }), _jsxs(DialogContent, { children: [_jsxs(Stack, { direction: "column", spacing: 2, sx: { my: 1 }, children: [_jsx(Typography, { variant: "h6", sx: { mb: 2 }, children: t("sprava-textu.massEdit.tagsChange", "Změna štítků") }), _jsx(Stack, { direction: "row", spacing: 2, sx: { alignItems: "center", mb: 2 }, children: _jsx(RenderTagsWithMenu, { namespace: taggerNamespace, tagIds: selectedTags, onAssign: (selectedIds) => setSelectedTags(selectedIds), addTagButtonLabel: t("sprava-textu.massEdit.selectTags", "Vybrat štítky"), alignButton: "right" }) }), _jsxs(Stack, { direction: "row", spacing: 2, sx: { alignItems: "center" }, children: [_jsx(Box, { sx: { flexGrow: 1 } }), _jsx(SmartButton, { variant: "outlined", color: "primary", onClick: () => {
                                            return removeTagsHandler(texts, selectedTags);
                                        }, children: t("sprava-textu.massEdit.removeTags", "Odebrat štítky") }), _jsx(SmartButton, { variant: "contained", color: "primary", onClick: () => {
                                            return addTagsHandler(texts, selectedTags);
                                        }, children: t("sprava-textu.massEdit.addTags", "Přidat štítky") })] }), _jsx(Divider, { sx: { my: 2 } }), _jsx(Typography, { variant: "h6", sx: { mb: 2 }, children: t("sprava-textu.massEdit.statusChange", "Změna stavu") }), _jsxs(Stack, { direction: "row", spacing: 2, sx: { alignItems: "center" }, children: [_jsx(Box, { sx: { flexGrow: 1 } }), _jsx(SmartButton, { variant: "outlined", color: "error", onClick: () => {
                                            return needReviewStatusHandler(texts);
                                        }, children: t("sprava-textu.massEdit.neededReviewStatus", "Nutná revize") })] }), _jsx(Divider, { sx: { my: 2 } }), _jsx(Typography, { variant: "h6", sx: { mb: 2 }, children: t("sprava-textu.massEdit.addVariant", "Přidat variantu") }), _jsx(SelectVariantUrns, { canEdit: true, data: urnsData, tree: tree, stacked: true, onChange: (data) => {
                                    return setUrnsData(data);
                                } }), _jsxs(Stack, { direction: "row", spacing: 2, sx: { alignItems: "center" }, children: [_jsx(Box, { sx: { flexGrow: 1 } }), _jsx(SmartButton, { variant: "contained", onClick: () => {
                                            return addVariantHandler(texts);
                                        }, children: t("sprava-textu.massEdit.addVariant", "Přidat variantu") })] }), showProgressBar && (_jsxs(_Fragment, { children: [_jsx(Divider, { sx: { my: 2 } }), _jsx(Typography, { variant: "h6", sx: { mb: 2 }, children: t("sprava-textu.massEdit.progress", "Průběh úprav") }), _jsx(LinearProgress, { value: progressValue, variant: "determinate" })] })), _jsx(Divider, { sx: { my: 2 } }), _jsxs(Stack, { direction: "row", spacing: 2, sx: {}, children: [_jsx(Button, { variant: "text", color: "primary", onClick: onDoneHandler, children: t("sprava-textu.massEdit.close", "Zavřít") }), _jsx(Box, { sx: { flexGrow: 1 } })] })] }), _jsx(AlertsBarContainer, { id: "alerts-in-dialog" })] })] }));
};
